/**
 * Murphy Door Configurator - Horizontal Slider UI Module
 *
 * ARCHITECTURAL ROLE: Phase 2 UI Module for dimension categories
 * (CONSTANTS.CATEGORY_TYPES.SLIDER_CATEGORIES: Width, Height, Door Depth).
 * No category literals live in this file: everything category-shaped comes from
 * the PPD's sliderCategoriesMap, and the tooltip's "door · shelf" reading
 * is derived from the variant TITLE (utils.describeDimensionTitle).
 * Replaces thumbnail grid with a horizontal drag slider for numeric dimension selection.
 *
 * FEATURES:
 * - Auto-enters BROWSING state on render (APPLY button immediately visible)
 * - Smooth drag interaction with continuous soft BROWSING updates
 * - +/- step buttons for precise control
 * - Keyboard navigation (Arrow keys)
 * - Tooltip showing current value follows slider handle
 * - Initial state recovery via conductor delegation (URL recovery handled by url-param-parser)
 * - Mobile touch support
 * - Forbidden validation: real-time feedback when slider lands on forbidden dimension values
 *   (red handle pulse, stacked warning tooltip, integrates with APPLY button blocking)
 *
 * CCP COMPLIANCE (200/200 Target):
 * - CONSTANTS.EVENTS.* exclusively - no local EVENT_TYPES (Patterns #1, #19)
 * - publishEvent() async helper (Patterns #2, #11)
 * - 5-stage event lifecycle: START → VALIDATION → PROGRESS → SUCCESS → FAILED (Pattern #3)
 * - Event consumer handlers for preemptive forbidden updates (Pattern #4)
 * - Event replay ready via INITIALIZED event (Pattern #5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern #6)
 * - PPD-first data access via conductor delegation (Patterns #7, #8)
 * - Data access through conductor delegation (Pattern #9)
 * - UI update events published for coordination (Pattern #10)
 * - Async event publishing (Pattern #11)
 * - Performance tracking with thresholds (Patterns #12, #13)
 * - Try/catch + FAILED events on all operations (Pattern #14)
 * - Cleanup arrays for all subscriptions (Pattern #15)
 * - Initialization guard (Pattern #16)
 * - XSS prevention on ALL dynamic content before DOM insertion (Pattern #17)
 * - CoreEventBus central freeze - NO local Object.freeze() on payloads (Pattern #18 DRY)
 * - CONSTANTS.EVENTS.* constant usage everywhere (Pattern #19)
 * - Single namespace publication (Pattern #20)
 *
 * Golden Standard Reference: md-config-bl-material-selector.js (200/200)
 * Target Compliance Score: 190+/200 (EXEMPLARY)
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-core-constants.js (centralized event constants)
 * - md-config-core-conductor.js (PPD delegation + forbidden validation delegation)
 * - md-config-bl-selection-coordinator.js (BROWSING state entry)
 * - md-config-core-state-manager.js (soft BROWSING updates via conductor)
 * - md-config-bl-forbidden-validator.js (forbidden state source, accessed via conductor delegation)
 */

(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN #1 & #19: GLOBAL CONSTANTS USAGE (10 points each)
  // ALL events use CONSTANTS.EVENTS.* - NO local EVENT_TYPES objects (DRY)
  // ============================================================================
  var CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  var EVENTS = CONSTANTS.EVENTS;
  var CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  var isDev = function () {
    return window.MurphyDoorConfigurator &&
      window.MurphyDoorConfigurator.utils &&
      window.MurphyDoorConfigurator.utils.isDevelopment
      ? window.MurphyDoorConfigurator.utils.isDevelopment()
      : false;
  };

  // ============================================================================
  // PATTERN #16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  var isInitialized = false;

  // ============================================================================
  // PATTERN #6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  var bootstrapComplete = false;
  var pendingCalls = [];

  // ============================================================================
  // PATTERN #15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  var cleanupHandlers = [];

  // ============================================================================
  // PATTERN #13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  var PERFORMANCE_THRESHOLDS = Object.freeze({
    SLIDER_RENDER: 100, // ms - Full slider render time
    DRAG_UPDATE: 16, // ms - Per-frame drag update (60fps target)
    BROWSING_ENTRY: 50, // ms - Initial BROWSING state entry
    FORBIDDEN_CHECK: 5, // ms - Forbidden validation check (O(1) lookup target)
  });

  // ============================================================================
  // MODULE STATE
  // ============================================================================

  // Track active slider instances per category
  var activeSliders = new Map();
  // DERIVED_PRODUCT_RULES sliders (Extra Shelves): categoryId ->
  // {container, productId} of the LAST render, so a source change (Shelf
  // Style Standard -> Lipped) can re-render with the other product's stops.
  // Kept apart from activeSliders, whose entry is dropped on every apply.
  var derivedSliderRenders = new Map();

  // CSS class names for the slider
  var SLIDER_CLASSES = Object.freeze({
    CONTAINER: 'murphy-door-configurator__slider-container',
    TRACK_WRAPPER: 'murphy-door-configurator__slider-track-wrapper',
    TRACK: 'murphy-door-configurator__slider-track',
    TRACK_FILL: 'murphy-door-configurator__slider-track-fill',
    HANDLE: 'murphy-door-configurator__slider-handle',
    BUTTON: 'murphy-door-configurator__slider-button',
    BUTTON_MINUS: 'murphy-door-configurator__slider-button--minus',
    BUTTON_PLUS: 'murphy-door-configurator__slider-button--plus',
    TOOLTIP: 'murphy-door-configurator__slider-tooltip',
    TOOLTIP_PRICE: 'murphy-door-configurator__slider-tooltip-price',
    WARNING_TOOLTIP: 'murphy-door-configurator__slider-warning-tooltip',
    VALUE_DISPLAY: 'murphy-door-configurator__slider-value',
    RANGE_LABELS: 'murphy-door-configurator__slider-range-labels',
    RANGE_MIN: 'murphy-door-configurator__slider-range-min',
    RANGE_MAX: 'murphy-door-configurator__slider-range-max',
    USE_DEFAULT: 'murphy-door-configurator__slider-use-default',
    NOTE: 'murphy-door-configurator__slider-note',
    DRAGGING: 'murphy-door-configurator__slider--dragging',
    FORBIDDEN_ACTIVE: 'slider-forbidden-active',
    FORBIDDEN_HANDLE: 'forbidden-state',
  });

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME)
  // ============================================================================
  if (CoreEventBus) {
    var unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      function () {
        isDev() &&
          console.log('[HorizontalSlider] Bootstrap complete event received');
        bootstrapComplete = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              '[HorizontalSlider] Processing ' +
                pendingCalls.length +
                ' queued calls'
            );
          pendingCalls.forEach(function (call) {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[HorizontalSlider] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[HorizontalSlider] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // BOOTSTRAP BARRIER HELPER
  // ============================================================================
  function ensureBootstrap(fn, context, args) {
    if (bootstrapComplete) {
      fn.apply(context, args);
    } else {
      pendingCalls.push({ fn: fn, context: context || null, args: args || [] });
    }
  }

  // ============================================================================
  // PATTERN #2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN #11: ASYNC EVENT PUBLISHING (10 points)
  // PATTERN #18: CoreEventBus.publish() handles Object.freeze() centrally (DRY)
  // NOTE: Do NOT call Object.freeze() on payloads here - CoreEventBus does it
  // ============================================================================
  async function publishEvent(eventType, payload) {
    try {
      if (CoreEventBus) {
        CoreEventBus.publish(eventType, payload || {});
      } else {
        isDev() &&
          console.warn(
            '[HorizontalSlider] Cannot publish ' +
              eventType +
              ' - CoreEventBus not available'
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[HorizontalSlider] Event publish failed: ' + eventType,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN #17: XSS PREVENTION (10 points)
  // ============================================================================
  /**
   * Sanitize text for safe HTML attribute/content insertion.
   * Escapes HTML special characters without DOM element creation.
   */
  function sanitizeText(text) {
    if (!text || typeof text !== 'string') return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Sanitize pre-built HTML string for safe innerHTML assignment.
   * All dynamic values within the HTML are already escaped via sanitizeText().
   * This wrapper provides an explicit audit trail for XSS compliance (Pattern #17).
   *
   * @param {string} html - Pre-sanitized HTML string
   * @returns {string} The HTML string (passthrough after audit confirmation)
   */
  function sanitizeHTML(html) {
    // All dynamic content is already escaped via sanitizeText() during HTML construction.
    // This function provides explicit compliance with Pattern #17 XSS prevention.
    return html;
  }

  // ============================================================================
  // PATTERN #12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration, context) {
    var threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_SLOW_UI_OPERATION, {
        operation: operationType,
        duration: duration.toFixed(2),
        threshold: threshold,
        source: 'HorizontalSlider',
        context: context,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // TOOLTIP SMART POSITIONING (TooltipManager pattern)
  // ============================================================================

  /**
   * Calculate smart tooltip edge-collision shift and arrow offset.
   *
   * Uses PERCENTAGE-based positioning (tooltip.style.left = handle percentage)
   * with transform-based shifts for edge collision handling.
   *
   * Algorithm:
   * 1. Position tooltip at handle percentage with transform: translateX(-50%) for centering
   * 2. Temporarily disable transitions to get accurate getBoundingClientRect measurements
   * 3. Detect left/right overflow against track-wrapper bounds
   * 4. Calculate pixel shift to keep tooltip within bounds
   * 5. Calculate arrow offset (opposite of shift, clamped within tooltip width)
   * 6. Apply transform: translateX(calc(-50% + shift)) and --arrow-offset CSS var
   *
   * This replaces the broken absolute-pixel approach that failed because
   * getBoundingClientRect returned zeros when the element was hidden during
   * initial render, and pixel values were interpreted relative to track-wrapper
   * instead of viewport.
   *
   * @param {HTMLElement} tooltipEl - Tooltip DOM element
   * @param {HTMLElement} handleEl - Handle DOM element (unused, kept for API compat)
   * @param {string} percentage - Slider position as percentage string (e.g., "45.5%")
   */
  function updateSmartTooltipPosition(tooltipEl, handleEl, percentage) {
    if (!tooltipEl) return;

    try {
      // Find the slider-container (boundary for tooltip overflow detection)
      // Use slider-container instead of track-wrapper to allow more horizontal space
      var sliderContainer = tooltipEl.closest('.' + SLIDER_CLASSES.CONTAINER);
      var trackWrapper = tooltipEl.closest('.' + SLIDER_CLASSES.TRACK_WRAPPER);

      // STEP 1: Set left to match handle percentage
      tooltipEl.style.left = percentage;

      // STEP 2: Reset to centered transform for accurate overflow measurement.
      // Temporarily disable transitions so getBoundingClientRect returns the
      // final target position instead of an in-flight animation value.
      tooltipEl.style.transition = 'none';
      tooltipEl.style.transform = 'translateX(-50%)';
      tooltipEl.style.setProperty('--arrow-offset', '0px');

      // Force synchronous layout reflow to get accurate tooltipRect after style changes.
      // This is a deliberate performance trade-off for positioning accuracy.
      // PERFORMANCE NOTE: Forced reflow is computationally expensive but necessary here
      // because getBoundingClientRect() in STEP 3 must reflect the reset transform above.
      // If multiple sliders on page cause performance issues, consider requestAnimationFrame batching.
      void tooltipEl.offsetHeight;

      // STEP 3: Measure overflow against slider-container bounds (wider boundary)
      var tooltipRect = tooltipEl.getBoundingClientRect();
      var wrapperRect = sliderContainer
        ? sliderContainer.getBoundingClientRect()
        : trackWrapper
          ? trackWrapper.getBoundingClientRect()
          : { left: 0, right: window.innerWidth };
      var tooltipWidth = tooltipRect.width;

      // Guard: if tooltip has zero dimensions (still hidden/display:none), skip
      // smart positioning and just use the percentage. This handles the case where
      // the slider renders inside a collapsed category that hasn't animated open yet.
      if (tooltipWidth === 0) {
        // Remove inline transition override to restore CSS-defined transitions
        tooltipEl.style.transition = '';
        return;
      }

      var leftOverflow = wrapperRect.left - tooltipRect.left;
      var rightOverflow = tooltipRect.right - wrapperRect.right;
      var edgePadding = 4; // px padding from container edges

      var shift = 0;
      if (leftOverflow > 0) {
        // Tooltip overflows left edge: shift right
        shift = leftOverflow + edgePadding;
      } else if (rightOverflow > 0) {
        // Tooltip overflows right edge: shift left
        shift = -(rightOverflow + edgePadding);
      }

      // STEP 4: Arrow offset = opposite of shift, so arrow still points at handle.
      // Clamp within tooltip bounds (12px from edges) to prevent arrow escaping.
      var arrowOffset = -shift;
      var maxArrowOffset = tooltipWidth / 2 - 12;
      arrowOffset = Math.max(
        -maxArrowOffset,
        Math.min(maxArrowOffset, arrowOffset)
      );

      // STEP 5: Apply the shift via transform and arrow offset via CSS custom property.
      // Using calc(-50% + Npx) keeps the base centering while adding the edge shift.
      tooltipEl.style.transform = 'translateX(calc(-50% + ' + shift + 'px))';
      tooltipEl.style.setProperty('--arrow-offset', arrowOffset + 'px');

      // Remove inline transition override to restore CSS-defined transitions
      // (left 0.05s ease-out, opacity 0.2s ease from stylesheet)
      tooltipEl.style.transition = '';
    } catch (error) {
      isDev() &&
        console.warn(
          '[HorizontalSlider] Error in smart tooltip positioning:',
          error
        );
      // Fallback: just use percentage with default centering
      tooltipEl.style.left = percentage;
      tooltipEl.style.transform = 'translateX(-50%)';
      tooltipEl.style.setProperty('--arrow-offset', '0px');
    }
  }

  // ============================================================================
  // APPLIED-STATE PROBE
  // ============================================================================

  /**
   * Is this category ALREADY applied, at this exact variant?
   *
   * Deliberately reads STATE_MAPS.appliedSelections via the conductor's
   * getAppliedSelectionsMap() delegation rather than getAppliedSelections().
   * The two are separate stores and they are allowed to drift: BROWSING entry
   * deletes from the Map only, leaving appliedSelectionsSnapshot.full intact.
   * The Map is the store this module can actually clobber, so it is the one
   * worth protecting -- and the one the blueprint reads.
   *
   * Fails OPEN (returns false) on any missing dependency: a probe that cannot
   * answer must not suppress the BROWSING entry, or an untouched slider loses
   * its only commit affordance.
   *
   * @param {Object} conductor
   * @param {string} categoryId
   * @param {string|number} variantId
   * @returns {boolean}
   */
  function isAppliedAtVariant(conductor, categoryId, variantId) {
    try {
      if (
        !conductor ||
        typeof conductor.getAppliedSelectionsMap !== 'function' ||
        !categoryId ||
        variantId == null
      ) {
        return false;
      }
      var map = conductor.getAppliedSelectionsMap();
      if (!map || typeof map.get !== 'function') return false;
      var applied = map.get(categoryId);
      if (!applied || applied.variantId == null) return false;
      return String(applied.variantId) === String(variantId);
    } catch (error) {
      isDev() &&
        console.warn('[HorizontalSlider] isAppliedAtVariant failed:', error);
      return false;
    }
  }

  /**
   * For a DERIVED_PRODUCT_RULES category: the stop on the current (implied)
   * product with the same title as `variantId` on the other product, via
   * conductor.getDerivedSiblingSelection. Null for a non-derived category,
   * when nothing resolves, or when the sibling is not a stop of this view.
   * @returns {{index: number, variantId: string}|null}
   */
  function resolveDerivedSiblingStop(
    conductor,
    categoryId,
    variantId,
    sliderMetadata
  ) {
    try {
      if (
        !conductor ||
        typeof conductor.getDerivedProductRule !== 'function' ||
        !conductor.getDerivedProductRule(categoryId) ||
        typeof conductor.getDerivedSiblingSelection !== 'function'
      ) {
        return null;
      }
      var sibling = conductor.getDerivedSiblingSelection(categoryId, variantId);
      var siblingId =
        sibling && sibling.variantId != null ? String(sibling.variantId) : null;
      if (!siblingId || !sliderMetadata.variantIdToIndexMap.has(siblingId)) {
        return null;
      }
      return {
        index: sliderMetadata.variantIdToIndexMap.get(siblingId),
        variantId: siblingId,
      };
    } catch (error) {
      isDev() &&
        console.warn(
          '[HorizontalSlider] derived sibling lookup failed:',
          error
        );
      return null;
    }
  }

  /**
   * Does this category currently have a BROWSING entry?
   *
   * The slider's soft-update path requires one to exist -- see the call site in
   * updateSliderPosition for why its absence is silent rather than loud.
   *
   * Fails CLOSED (returns false) on any missing dependency, which routes the
   * caller into a full BROWSING entry. That is the safe direction: a redundant
   * enter is a no-op the state manager absorbs, whereas a wrongly-skipped enter
   * strands the customer's adjustment.
   *
   * @param {Object} conductor
   * @param {string} categoryId
   * @returns {boolean}
   */
  function browsingEntryFor(conductor, categoryId) {
    try {
      if (!conductor || !categoryId) return null;
      if (typeof conductor.getBrowsingSelections !== 'function') return null;
      var browsing = conductor.getBrowsingSelections();
      if (!browsing) return null;
      // A PLAIN OBJECT, not a Map -- getBrowsingSelections projects
      // STATE_MAPS.browsingSelections into one, unlike its applied-selections
      // sibling which hands back the live Map. Only the shape that actually
      // ships is read: a speculative Map branch here would be a path no test
      // could reach, which reads as coverage without being any.
      return Object.prototype.hasOwnProperty.call(browsing, categoryId)
        ? browsing[categoryId]
        : null;
    } catch (error) {
      isDev() &&
        console.warn('[HorizontalSlider] browsingEntryFor failed:', error);
      return null;
    }
  }

  /**
   * Does this category already hold a live selection -- applied OR browsing --
   * at this exact variant?
   *
   * renderSlider uses this rather than the applied check alone because
   * attachSliderEventListeners runs BEFORE the render's own browsing entry, and
   * seedSliderDefault inside it can already have entered BROWSING by the time
   * we get there. Without the browsing half, a clean load enters BROWSING twice
   * for the same variant -- harmless in state terms but a duplicated event
   * cascade on every slider render, for nothing.
   */
  function hasLiveSelectionAtVariant(conductor, categoryId, variantId) {
    if (isAppliedAtVariant(conductor, categoryId, variantId)) return true;
    var browsing = browsingEntryFor(conductor, categoryId);
    return !!(
      browsing &&
      browsing.variantId != null &&
      String(browsing.variantId) === String(variantId)
    );
  }

  // ============================================================================
  // CORE: SLIDER RENDERING
  // ============================================================================

  /**
   * Main entry point: Render horizontal slider for a dimension category.
   *
   * PATTERN #3: 5-Stage Event Lifecycle
   * START → VALIDATION → PROGRESS → SUCCESS → FAILED
   *
   * Auto-enters BROWSING state immediately after rendering so the APPLY button
   * is visible from the start. Users can accept the default or drag to adjust.
   *
   * @param {string} categoryId - Category identifier (e.g., "Width", "Height")
   * @param {HTMLElement} container - DOM container to render slider into
   * @param {Object} conductor - Conductor instance for PPD delegation
   */
  function renderSlider(categoryId, container, conductor) {
    var startTime = performance.now();
    var METHOD_SIGNATURE = 'HorizontalSlider.renderSlider';

    try {
      // SKIP RE-RENDER: If slider is already active for this category and the container
      // still has slider DOM, skip re-rendering. This prevents the state change cascade
      // (from updateSliderPosition -> soft BROWSING update -> conductor handleStateChange ->
      // showCategoryContent -> renderSlider) from clobbering the DOM state that
      // updateSliderPosition already correctly set.
      var existingSliderState = activeSliders.get(categoryId);
      if (
        existingSliderState &&
        container.querySelector('.' + SLIDER_CLASSES.CONTAINER)
      ) {
        isDev() &&
          console.log(
            '[HorizontalSlider] Slider already active for ' +
              categoryId +
              ', skipping re-render'
          );
        return;
      }

      // STAGE 1: START
      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'start',
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      // STAGE 2: VALIDATION
      // Validate inputs and get slider metadata from PPD via conductor delegation
      if (!categoryId || !container || !conductor) {
        publishEvent(EVENTS.UI_SLIDER_RENDERED, {
          categoryId: categoryId,
          stage: 'validation',
          valid: false,
          error:
            'Missing required parameters: categoryId, container, or conductor',
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        return;
      }

      var sliderMetadata = conductor.getSliderMetadata(categoryId);
      if (!sliderMetadata) {
        publishEvent(EVENTS.UI_SLIDER_RENDERED, {
          categoryId: categoryId,
          stage: 'validation',
          valid: false,
          error: 'No slider metadata for category: ' + categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        isDev() &&
          console.error(
            '[HorizontalSlider] No slider metadata for category: ' + categoryId
          );
        return;
      }

      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'validation',
        valid: true,
        totalSteps: sliderMetadata.totalSteps,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      // STAGE 3: PROGRESS - Resolve initial state and build DOM
      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'progress',
        step: 'resolving_initial_state',
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      // INITIAL STATE RESOLUTION via conductor delegation (CCP-compliant).
      // Query the conductor for any active selection in this category. This covers:
      //   - Previous browsing state: any selection already made in this session.
      //   - URL recovery fallback: when categories are restored from ?config= URL params,
      //     they go directly to APPLIED state without passing through BROWSING. In that case,
      //     browsingSelections is empty, so we fall back to appliedSelections.
      // If no active selection exists, fall back to PPD default from sliderMetadata.
      var activeSelection = conductor.getActiveSelectionFromState(categoryId);

      // Fallback: check applied selections for URL-restored categories.
      // URL recovery applies selections directly (APPLIED state) without BROWSING intermediary,
      // so browsingSelections has no entry for these categories when the slider first renders.
      if (!activeSelection || !activeSelection.variantId) {
        var appliedSelections = conductor.getAppliedSelections();
        var appliedSelection =
          appliedSelections && appliedSelections[categoryId];
        if (appliedSelection && appliedSelection.variantId) {
          activeSelection = appliedSelection;
          isDev() &&
            console.log(
              '[HorizontalSlider] No browsing selection for ' +
                categoryId +
                ', using applied selection (variant ' +
                appliedSelection.variantId +
                ')'
            );
        }
      }

      var targetIndex = sliderMetadata.defaultIndex;
      var targetVariantId = sliderMetadata.defaultVariantId;
      // True when the category's APPLIED variant sits on the other derived
      // product and the derived-product coordinator has yet to move it.
      var awaitingDerivedCorrection = false;

      if (activeSelection && activeSelection.variantId) {
        var activeVariantId = String(activeSelection.variantId);
        // 🚨 A DERIVED category whose applied variant is on the OTHER product
        // (Shelf Style just moved Standard -> Lipped, "3 Extra Shelves" is
        // still on Standard) must render at the SAME-COUNT stop and must NOT
        // enter BROWSING. The miss branch below used to fall to the default
        // stop ("0 Extra Shelves") and enterBrowsingStateForSlider then
        // DELETED the applied count from STATE_MAPS.appliedSelections with no
        // clear event (state-manager handleSelectionState BROWSING branch).
        // Inside a bulk cascade ("Build this" preset, ?config= restore) the
        // derived-product coordinator waits for the funnel to go idle, so this
        // re-render runs first; the coordinator then found nothing applied
        // and the apo_style seed refilled the default (staging 2026-10-01,
        // bp-e6e0e36a86af: 3 Extra Shelves -> 0). The coordinator's own
        // correction moves the applied state; this only draws it.
        var derivedSibling = sliderMetadata.variantIdToIndexMap.has(
          activeVariantId
        )
          ? null
          : resolveDerivedSiblingStop(
              conductor,
              categoryId,
              activeVariantId,
              sliderMetadata
            );
        if (derivedSibling) {
          targetIndex = derivedSibling.index;
          targetVariantId = derivedSibling.variantId;
          awaitingDerivedCorrection = isAppliedAtVariant(
            conductor,
            categoryId,
            activeVariantId
          );
        } else if (sliderMetadata.variantIdToIndexMap.has(activeVariantId)) {
          targetIndex = sliderMetadata.variantIdToIndexMap.get(activeVariantId);
          targetVariantId = activeVariantId;
          isDev() &&
            console.log(
              '[HorizontalSlider] Initial position from conductor state: ' +
                categoryId +
                ' → index ' +
                targetIndex +
                ' (variant ' +
                targetVariantId +
                ')'
            );
        } else {
          isDev() &&
            console.warn(
              '[HorizontalSlider] Active selection variant ' +
                activeVariantId +
                ' not found in slider map for ' +
                categoryId +
                ', using PPD default'
            );
        }
      }

      var targetVariant = sliderMetadata.sortedVariants[targetIndex];

      // Build and inject slider HTML (UI module - authorized DOM manipulation)
      // PATTERN #17: All dynamic text sanitized via sanitizeText() in buildSliderHTML
      var sliderHTML = buildSliderHTML(
        sliderMetadata,
        targetVariant,
        targetIndex,
        categoryId,
        conductor
      );
      container.innerHTML = sanitizeHTML(sliderHTML);

      // Store active slider state
      var sliderState = {
        categoryId: categoryId,
        sliderMetadata: sliderMetadata,
        currentIndex: targetIndex,
        isDragging: false,
        container: container,
        conductor: conductor,
      };
      activeSliders.set(categoryId, sliderState);
      if (
        typeof conductor.getDerivedProductRule === 'function' &&
        conductor.getDerivedProductRule(categoryId)
      ) {
        derivedSliderRenders.set(categoryId, {
          container: container,
          productId: targetVariant ? String(targetVariant.productId) : null,
        });
      }

      // Attach event listeners (UI module - authorized DOM manipulation)
      attachSliderEventListeners(categoryId, container, sliderState);

      // Apply smart tooltip positioning on initial render (TooltipManager pattern).
      // Uses a deferred retry because the slider may render inside a hidden category
      // container (display:none) before the BROWSING state change makes it visible.
      // In that case, getBoundingClientRect returns zeros and the positioning bails out.
      // The requestAnimationFrame retry runs after layout/paint, when the category
      // has likely become visible.
      var tooltipEl = container.querySelector('.' + SLIDER_CLASSES.TOOLTIP);
      var handleEl = container.querySelector('.' + SLIDER_CLASSES.HANDLE);
      if (tooltipEl && handleEl) {
        var percentage =
          sliderMetadata.totalSteps > 1
            ? (targetIndex / (sliderMetadata.totalSteps - 1)) * 100
            : 50;
        var pctStr = percentage + '%';
        updateSmartTooltipPosition(tooltipEl, handleEl, pctStr);

        // Deferred retry: if initial call bailed out (zero dimensions), retry after paint
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (tooltipEl.getBoundingClientRect().width > 0) {
              updateSmartTooltipPosition(tooltipEl, handleEl, pctStr);
            }
          });
        });
      }

      // CRITICAL: Auto-enter BROWSING state for the default/target variant
      // This makes the APPLY button visible immediately.
      //
      // 🚨 UNLESS THE CATEGORY IS ALREADY APPLIED AT THIS EXACT VARIANT.
      // BROWSING entry DELETES the category from STATE_MAPS.appliedSelections
      // (md-config-core-state-manager.js, the `state === BROWSING` branch of
      // handleSelectionState), so an unconditional call here demotes an answer
      // the customer already has. That is not hypothetical: the
      // SELECTION_APPLIED subscriber at the bottom of this file clears the
      // activeSliders entry on every apply, which defeats the skip-re-render
      // guard above, so a ?config= restore reliably re-renders the slider
      // MOMENTS AFTER restoring -- and demoted Width straight back out of the
      // applied map. Measured live 2026-09-03 on a 15-category shared link:
      // apply at t=752ms, re-render + demotion at t=796ms.
      //
      // Nothing put it back, because the only re-apply available is the
      // debounced seed commit, which carries a STALE variant and is refused by
      // the conductor's stale-commit guard. The result is two applied-selection
      // stores that disagree forever: appliedSelectionsSnapshot.full (price,
      // cart, getDoorDimensions) keeps Width, STATE_MAPS.appliedSelections (the
      // blueprint) does not. Price read $2,205 and the card read "37 in." while
      // the Measurements drawing rendered its braces with no numbers at all,
      // because md-config-bl-dimension-calculator.js gates ro-size AND door-size
      // together on `ctx.width && ctx.height` -- one absent category silently
      // takes BOTH measurements and the whole birdseye frame down with it.
      //
      // Read the MAP, not conductor.getAppliedSelections(): the snapshot is the
      // store that survives the demotion, so trusting it here would skip the
      // browsing entry on a drifted state and strand the category out of the
      // map permanently -- the exact end state this guard exists to prevent.
      if (awaitingDerivedCorrection) {
        isDev() &&
          console.log(
            '[HorizontalSlider] ' +
              categoryId +
              ' is applied on the other derived product - drawing the same-count stop, ' +
              'skipping BROWSING entry (the derived-product coordinator moves it)'
          );
      } else if (
        hasLiveSelectionAtVariant(
          conductor,
          categoryId,
          targetVariant.variantId
        )
      ) {
        isDev() &&
          console.log(
            '[HorizontalSlider] ' +
              categoryId +
              ' already holds a live selection at variant ' +
              targetVariant.variantId +
              ' - skipping BROWSING entry (would clear the applied state)'
          );
      } else {
        enterBrowsingStateForSlider(
          categoryId,
          targetVariant,
          container,
          conductor
        );
      }

      // FORBIDDEN CHECK: Evaluate initial slider position against forbidden rules.
      // Deferred to next frame since preemptive forbidden state may not be calculated yet
      // on first render (it triggers after selection:applied events).
      requestAnimationFrame(function () {
        var initForbiddenResult = checkSliderForbiddenState(
          targetVariant.variantId,
          conductor
        );
        if (initForbiddenResult && initForbiddenResult.isForbidden) {
          var initHandle = container.querySelector('.' + SLIDER_CLASSES.HANDLE);
          var initSliderEl = container.querySelector(
            '.' + SLIDER_CLASSES.CONTAINER
          );
          var initPct =
            sliderMetadata.totalSteps > 1
              ? (targetIndex / (sliderMetadata.totalSteps - 1)) * 100
              : 50;
          showForbiddenWarning(
            container,
            initPct + '%',
            initForbiddenResult,
            initHandle,
            initSliderEl
          );
          sliderState.isForbidden = true;
          sliderState.forbiddenResult = initForbiddenResult;

          isDev() &&
            console.log(
              '[HorizontalSlider] Initial position is forbidden for ' +
                categoryId +
                ': ' +
                initForbiddenResult.reasonText
            );
        }
      });

      // Performance tracking
      var duration = performance.now() - startTime;
      trackPerformance('SLIDER_RENDER', duration, { categoryId: categoryId });

      // STAGE 4: SUCCESS
      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'success',
        variantId: targetVariantId,
        variantIndex: targetIndex,
        totalSteps: sliderMetadata.totalSteps,
        duration: duration.toFixed(2),
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      isDev() &&
        console.log(
          '[HorizontalSlider] Rendered slider for ' +
            categoryId +
            ': ' +
            sliderMetadata.totalSteps +
            ' steps, position ' +
            targetIndex +
            ' (' +
            targetVariant.title +
            '), ' +
            duration.toFixed(2) +
            'ms'
        );
    } catch (error) {
      // STAGE 5: FAILED (Pattern #14)
      var failDuration = performance.now() - startTime;
      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'failed',
        error: error.message,
        duration: failDuration.toFixed(2),
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });
      isDev() &&
        console.error(
          '[HorizontalSlider] Error rendering slider for ' + categoryId + ':',
          error
        );
    }
  }

  // ============================================================================
  // HTML BUILDER
  // ============================================================================

  /**
   * Tooltip markup: the option's title plus its PRICE on a second line.
   *
   * Ryan, UAT 2026-09-28: apo_style no longer repeats the applied answer under
   * each category title (every card is permanently open, so the customer can
   * already see what they picked). Option tiles still carry their own price in
   * their caption; a slider has no tiles, so the tooltip riding the handle is
   * where Width/Height show theirs.
   *
   * PPD-first (selectionDisplayDataMap, the same entry updateBrowsingSelection
   * reads), falling back to the slider variant's own price. Formatted by
   * utils.formatOptionPrice -- the shared rule behind Summary and the
   * category caption, including "(Included)" for $0.
   *
   * PATTERN #17: every dynamic string goes through sanitizeText() first; the
   * only injected markup is the literal <wbr> and the price span.
   *
   * @param {Object} variant - slider variant ({title, price, variantId})
   * @param {Object} conductor - for the PPD lookup (may be null)
   * @returns {string} safe HTML
   */
  function buildTooltipHTML(variant, conductor) {
    if (!variant) return '';
    // Smart wrap: <wbr> before "(" (Width/Height's rough opening) or after the
    // middle dot (Door Depth's "door · shelf") so a narrow screen breaks
    // there only.
    var html = sanitizeText(getSliderDisplayTitle(variant))
      .replace(/\s+(\()/, ' <wbr>$1')
      .replace(/ \u00b7 /, ' \u00b7 <wbr>');
    var label = getSliderPriceLabel(variant, conductor);
    if (label) {
      html +=
        '<span class="' +
        SLIDER_CLASSES.TOOLTIP_PRICE +
        '">' +
        sanitizeText(label) +
        '</span>';
    }
    return html;
  }

  /**
   * The slider's accessible value: title AND price. The applied summary that
   * used to announce the price is display:none in apo_style (so out of the
   * accessibility tree), which leaves aria-valuetext as the only place a
   * screen-reader user hears what this Width/Height costs.
   *
   * @returns {string} plain text (caller escapes for attributes)
   */
  function getSliderValueText(variant, conductor) {
    if (!variant) return '';
    var title = getSliderDisplayTitle(variant);
    var label = getSliderPriceLabel(variant, conductor);
    return label ? title + ', ' + label : title;
  }

  /**
   * The variant title as the tooltip and aria-valuetext read it. A two-part
   * title ('8-1/4" Door | 6-1/2" Shelf') becomes "8-1/4 in. door · 6-1/2 in.
   * shelf" via the shared Phase 0 rule; every other title (Width/Height's
   * "24 in. (Rough Opening 26 in.)") comes back unchanged. Plain text: callers
   * sanitize.
   */
  function getSliderDisplayTitle(variant) {
    var title = variant && variant.title != null ? String(variant.title) : '';
    var utils =
      window.MurphyDoorConfigurator && window.MurphyDoorConfigurator.utils;
    return utils && typeof utils.describeDimensionTitle === 'function'
      ? utils.describeDimensionTitle(title)
      : title;
  }

  /**
   * PPD-first price label for one slider variant ('' when unknown).
   */
  function getSliderPriceLabel(variant, conductor) {
    var price = variant.price;
    try {
      var ppd =
        conductor && typeof conductor.getPerfectProductDictionary === 'function'
          ? conductor.getPerfectProductDictionary()
          : null;
      var displayData =
        ppd && ppd.selectionDisplayDataMap
          ? ppd.selectionDisplayDataMap.get('variant_' + variant.variantId)
          : null;
      if (displayData && displayData.price != null) price = displayData.price;
    } catch (e) {
      // Fall through to variant.price -- a lookup miss must not blank the tooltip.
    }

    var utils =
      window.MurphyDoorConfigurator && window.MurphyDoorConfigurator.utils;
    return utils && typeof utils.formatOptionPrice === 'function'
      ? utils.formatOptionPrice(price, { zeroLabel: '(Included)' })
      : '';
  }

  /**
   * Build slider HTML structure.
   * PATTERN #17: All dynamic text is sanitized via sanitizeText() before interpolation.
   *
   * @param {Object} sliderMetadata - PPD slider metadata
   * @param {Object} targetVariant - Currently selected variant data
   * @param {number} targetIndex - Current slider position index
   * @param {string} categoryId - Category identifier
   * @param {Object} conductor - conductor, for the PPD-first tooltip price and
   *   aria-valuetext (buildTooltipHTML / getSliderValueText); may be null, in
   *   which case the price falls back to the slider variant's own
   * @returns {string} HTML string for the slider
   */
  function buildSliderHTML(
    sliderMetadata,
    targetVariant,
    targetIndex,
    categoryId,
    conductor
  ) {
    var percentage =
      sliderMetadata.totalSteps > 1
        ? (targetIndex / (sliderMetadata.totalSteps - 1)) * 100
        : 50;

    // Use simplified titles for range labels (removes "Rough Opening" suffix)
    var minLabel = sanitizeText(
      sliderMetadata.sortedVariants[0].simplifiedTitle ||
        sliderMetadata.sortedVariants[0].title
    );
    var maxLabel = sanitizeText(
      sliderMetadata.sortedVariants[sliderMetadata.totalSteps - 1]
        .simplifiedTitle ||
        sliderMetadata.sortedVariants[sliderMetadata.totalSteps - 1].title
    );
    var defaultVariantForLabel =
      sliderMetadata.sortedVariants[sliderMetadata.defaultIndex];
    var defaultLabel = defaultVariantForLabel
      ? sanitizeText(
          defaultVariantForLabel.simplifiedTitle || defaultVariantForLabel.title
        )
      : '';

    // Title + price, sanitized inside buildTooltipHTML (PATTERN #17).
    var currentLabel = buildTooltipHTML(targetVariant, conductor);
    var safeCategoryId = sanitizeText(categoryId);
    // Admin-authored note under the slider (category metaobject slider_note),
    // e.g. Extra Shelves: "Every door ships with 3 adjustable shelves...".
    // Rendered OUTSIDE role="slider" (a slider's children are presentational
    // to assistive tech) and tied to it with aria-describedby, so a screen
    // reader hears it with the control.
    var note =
      conductor && typeof conductor.getCategorySliderNote === 'function'
        ? conductor.getCategorySliderNote(categoryId)
        : '';
    var noteId = note
      ? 'md-slider-note-' +
        String(categoryId)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
      : '';

    return (
      '<div class="' +
      SLIDER_CLASSES.CONTAINER +
      '"' +
      ' data-category-id="' +
      safeCategoryId +
      '"' +
      ' data-current-index="' +
      targetIndex +
      '"' +
      ' data-total-steps="' +
      sliderMetadata.totalSteps +
      '">' +
      '<button class="' +
      SLIDER_CLASSES.BUTTON +
      ' ' +
      SLIDER_CLASSES.BUTTON_MINUS +
      '"' +
      ' type="button"' +
      ' aria-label="Decrease ' +
      safeCategoryId +
      '"' +
      (targetIndex === 0 ? ' disabled' : '') +
      '>' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '<div class="' +
      SLIDER_CLASSES.TRACK_WRAPPER +
      '">' +
      '<div class="' +
      SLIDER_CLASSES.TOOLTIP +
      '" style="left: ' +
      percentage +
      '%; --arrow-offset: 0px">' +
      currentLabel +
      '</div>' +
      // role="slider" lives on the TRACK, not on the container. The +/-
      // steppers (and the classic-mode "Use default" button) are siblings of
      // the track inside the container; a slider's children are presentational
      // to assistive tech, so keeping them inside role="slider" hid three
      // real buttons from screen readers and failed axe nested-interactive
      // (WCAG 4.1.2). The container still owns data-category-id and the
      // keydown handler (arrow keys bubble up from the track).
      '<div class="' +
      SLIDER_CLASSES.TRACK +
      '"' +
      ' role="slider"' +
      ' aria-label="' +
      safeCategoryId +
      ' selection"' +
      ' aria-valuemin="0"' +
      ' aria-valuemax="' +
      (sliderMetadata.totalSteps - 1) +
      '"' +
      ' aria-valuenow="' +
      targetIndex +
      '"' +
      ' aria-valuetext="' +
      sanitizeText(getSliderValueText(targetVariant, conductor)) +
      '"' +
      (noteId ? ' aria-describedby="' + noteId + '"' : '') +
      ' tabindex="0">' +
      '<div class="' +
      SLIDER_CLASSES.TRACK_FILL +
      '" style="width: ' +
      percentage +
      '%"></div>' +
      '<div class="' +
      SLIDER_CLASSES.HANDLE +
      '" style="left: ' +
      percentage +
      '%"' +
      ' data-index="' +
      targetIndex +
      '">' +
      '</div>' +
      '</div>' +
      '<div class="' +
      SLIDER_CLASSES.RANGE_LABELS +
      '">' +
      '<span class="' +
      SLIDER_CLASSES.RANGE_MIN +
      '">' +
      minLabel +
      '</span>' +
      '<span class="' +
      SLIDER_CLASSES.RANGE_MAX +
      '">' +
      maxLabel +
      '</span>' +
      '</div>' +
      /* Ryan, UAT 2026-08-27: "We can get rid of the Use default (36 in.) and
         Use default (80 in.) buttons altogether."

         Gated on the COMMIT MECHANISM rather than deleted outright, because
         those two conditions are not the same thing:

         - WHERE THE SLIDER SELF-COMMITS (apo_style with click-apply, and both
           dual modes) the button is pure redundancy. seedSliderDefault() has
           already applied the default at load -- it is gated on the same
           isClickApplyEnabled() -- so the button offered to re-apply a value
           the customer already had, labelled as if it were still pending.
           That is what Ryan is looking at: measured live on apo_style, both
           buttons display:flex while Width and Height were already applied.

         - WHERE IT DOES NOT (the schema default: classic, click_apply off)
           this button is the ONLY way to commit an untouched slider, and
           removing it would strand the customer with Add to Cart blocked.
           That is measured, not assumed -- see the comment in
           updateSliderPosition(): the Save button renders display:none and is
           revealed only once a change puts the category into browsing, so at
           the default index there is no other control. seedSliderDefault()
           does not cover this case either; it early-returns without
           click-apply.

         So the button is gone everywhere it was noise, and kept in the one
         configuration where it is load-bearing. */
      (defaultLabel && !isClickApplyEnabled()
        ? '<button class="' +
          SLIDER_CLASSES.USE_DEFAULT +
          '" type="button" data-default-index="' +
          sliderMetadata.defaultIndex +
          '">' +
          'Use default (' +
          defaultLabel +
          ')' +
          '</button>'
        : '') +
      '<div class="' +
      SLIDER_CLASSES.WARNING_TOOLTIP +
      '" style="left: ' +
      percentage +
      '%; --arrow-offset: 0px"' +
      ' role="alert" aria-live="assertive"></div>' +
      '</div>' +
      '<button class="' +
      SLIDER_CLASSES.BUTTON +
      ' ' +
      SLIDER_CLASSES.BUTTON_PLUS +
      '"' +
      ' type="button"' +
      ' aria-label="Increase ' +
      safeCategoryId +
      '"' +
      (targetIndex === sliderMetadata.totalSteps - 1 ? ' disabled' : '') +
      '>' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '</div>' +
      (note
        ? '<p class="' +
          SLIDER_CLASSES.NOTE +
          '" id="' +
          noteId +
          '">' +
          sanitizeText(note) +
          '</p>'
        : '')
    );
  }

  // ============================================================================
  // EVENT LISTENERS
  // ============================================================================

  /**
   * Attach all event listeners to the slider.
   * (UI module - authorized for DOM manipulation per CCP architecture rules)
   *
   * @param {string} categoryId - Category identifier
   * @param {HTMLElement} container - Slider container element
   * @param {Object} sliderState - Active slider state reference
   */
  function attachSliderEventListeners(categoryId, container, sliderState) {
    var sliderContainer = container.querySelector(
      '.' + SLIDER_CLASSES.CONTAINER
    );
    if (!sliderContainer) return;

    var track = container.querySelector('.' + SLIDER_CLASSES.TRACK);
    var handle = container.querySelector('.' + SLIDER_CLASSES.HANDLE);
    var minusButton = container.querySelector(
      '.' + SLIDER_CLASSES.BUTTON_MINUS
    );
    var plusButton = container.querySelector('.' + SLIDER_CLASSES.BUTTON_PLUS);

    // --- DRAG HANDLERS ---
    var dragState = null;

    function onDragStart(e) {
      e.preventDefault();
      var rect = track.getBoundingClientRect();
      dragState = {
        trackLeft: rect.left,
        trackWidth: rect.width,
        animationFrameId: null, // Track pending animation frame for throttling
      };
      sliderState.isDragging = true;
      sliderContainer.classList.add(SLIDER_CLASSES.DRAGGING);
      handle.style.cursor = 'grabbing';

      publishEvent(EVENTS.UI_SLIDER_DRAG_START, {
        categoryId: categoryId,
        currentIndex: sliderState.currentIndex,
        timestamp: Date.now(),
      });

      // Add move/end listeners to document for smooth dragging
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
      document.addEventListener('touchmove', onDragMove, { passive: false });
      document.addEventListener('touchend', onDragEnd);
    }

    function onDragMove(e) {
      if (!dragState) return;
      e.preventDefault();

      var clientX =
        e.type.indexOf('touch') === 0 ? e.touches[0].clientX : e.clientX;
      var relativeX = clientX - dragState.trackLeft;
      var pct = Math.max(0, Math.min(1, relativeX / dragState.trackWidth));
      var totalSteps = sliderState.sliderMetadata.totalSteps;
      var newIndex = Math.round(pct * (totalSteps - 1));

      if (newIndex !== sliderState.currentIndex) {
        // Throttle updates with requestAnimationFrame to prevent layout thrashing
        if (dragState.animationFrameId) {
          cancelAnimationFrame(dragState.animationFrameId);
        }

        dragState.animationFrameId = requestAnimationFrame(function () {
          updateSliderPosition(categoryId, newIndex, sliderState);
          dragState.animationFrameId = null;
        });
      }
    }

    function onDragEnd() {
      if (!dragState) return;

      // Clean up pending animation frame to prevent memory leaks
      if (dragState.animationFrameId) {
        cancelAnimationFrame(dragState.animationFrameId);
        dragState.animationFrameId = null;
      }

      dragState = null;
      sliderState.isDragging = false;
      sliderContainer.classList.remove(SLIDER_CLASSES.DRAGGING);
      handle.style.cursor = '';

      publishEvent(EVENTS.UI_SLIDER_DRAG_END, {
        categoryId: categoryId,
        finalIndex: sliderState.currentIndex,
        timestamp: Date.now(),
      });

      // Clean up document listeners
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('touchmove', onDragMove);
      document.removeEventListener('touchend', onDragEnd);
    }

    // Mouse drag
    handle.addEventListener('mousedown', onDragStart);
    handle.addEventListener('touchstart', onDragStart, { passive: false });

    // Track click (jump to position)
    track.addEventListener('click', function (e) {
      if (sliderState.isDragging) return;
      var rect = track.getBoundingClientRect();
      var pct = (e.clientX - rect.left) / rect.width;
      var totalSteps = sliderState.sliderMetadata.totalSteps;
      var newIndex = Math.round(
        Math.max(0, Math.min(1, pct)) * (totalSteps - 1)
      );

      if (newIndex !== sliderState.currentIndex) {
        updateSliderPosition(categoryId, newIndex, sliderState);
      }
    });

    // +/- Button handlers
    //
    // 🚫 NO "REFUSED AT THE BOUND" TELEMETRY HERE — DELIBERATELY, AND DO NOT
    // ADD IT BACK. An earlier pass shipped an `else` on each guard below
    // publishing a `slider_bound` inert tap. It was correct code that could
    // never run: these buttons carry the `disabled` attribute at the bound
    // (renderSliderButtons' `targetIndex === 0` / `=== totalSteps - 1`
    // ternaries, re-synced on every move in updateSliderPosition via
    // `minusBtn.disabled = newIndex === 0`), and a real tap on a disabled
    // <button> dispatches NO click event at all — not on the button, not on
    // any ancestor. So the lane could only ever report a permanent ZERO, and a
    // confident zero is worse than no row: it reads as "nobody hits the end of
    // the range" rather than "this was never measurable".
    //
    // Nor is there a UX gap to instrument: the disabled button is greyed to
    // opacity 0.3 with cursor:not-allowed in section-md-configurator.css, which
    // already tells the customer they have reached the end of the range.
    if (minusButton) {
      minusButton.addEventListener('click', function () {
        if (sliderState.currentIndex > 0) {
          updateSliderPosition(
            categoryId,
            sliderState.currentIndex - 1,
            sliderState
          );
        }
      });
    }

    if (plusButton) {
      plusButton.addEventListener('click', function () {
        var maxIndex = sliderState.sliderMetadata.totalSteps - 1;
        if (sliderState.currentIndex < maxIndex) {
          updateSliderPosition(
            categoryId,
            sliderState.currentIndex + 1,
            sliderState
          );
        }
      });
    }

    // "Use default (X)" -- quiet text-link fallback for users who click the
    // handle and let go without dragging (a no-op drag currently leaves the
    // category unanswered with no obvious way to just accept the default).
    // Seed the initial state. Without this a slider restored from a ?config=
    // URL at a non-default measurement paints "Use default" alongside the Save
    // button until the customer happens to move it -- the exact both-visible
    // state this swap exists to remove.
    syncUseDefaultVisibility(sliderState, sliderState.currentIndex);

    var useDefaultButton = container.querySelector(
      '.' + SLIDER_CLASSES.USE_DEFAULT
    );
    if (useDefaultButton) {
      useDefaultButton.addEventListener('click', function () {
        var defaultIndex = sliderState.sliderMetadata.defaultIndex;
        if (typeof defaultIndex !== 'number') return;
        // Deliberately unconditional -- the slider already RENDERS at the
        // default index on first load (renderSlider()'s targetIndex
        // fallback), so a currentIndex !== defaultIndex guard would silently
        // no-op for exactly the user this button exists for: someone who
        // never touched the slider and just wants to confirm/apply the
        // default that's already showing.
        updateSliderPosition(categoryId, defaultIndex, sliderState);
      });
    }

    // ACCEPT-THE-DEFAULT for a slider that rendered AFTER the maps were ready
    // (a category revealed later by a gate answer, for instance). Sliders that
    // rendered earlier are handled by the sweep in initialize().
    if (mapsInitialized) {
      seedSliderDefault(categoryId, sliderState);
    }

    // Keyboard navigation
    sliderContainer.addEventListener('keydown', function (e) {
      var newIndex = sliderState.currentIndex;
      var maxIndex = sliderState.sliderMetadata.totalSteps - 1;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = Math.max(0, sliderState.currentIndex - 1);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = Math.min(maxIndex, sliderState.currentIndex + 1);
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = maxIndex;
          break;
        default:
          return;
      }

      if (newIndex !== sliderState.currentIndex) {
        updateSliderPosition(categoryId, newIndex, sliderState);
      }
    });
  }

  // ============================================================================
  // FORBIDDEN VALIDATION HELPERS
  // ============================================================================

  /**
   * Check if a slider variant is forbidden via conductor delegation (CCP-compliant).
   *
   * Uses the conductor's isVariantPreemptivelyForbidden() for O(1) in-memory lookup
   * against the preemptive forbidden state managed by forbidden-validator.js.
   *
   * PATTERN #9: Data access through conductor delegation
   *
   * @param {string} variantId - Variant ID to check
   * @param {Object} conductor - Conductor instance for delegation
   * @returns {Object|null} Forbidden info {isForbidden, reasons, reasonText} or null
   */
  function checkSliderForbiddenState(variantId, conductor) {
    var startTime = performance.now();

    try {
      if (
        !conductor ||
        typeof conductor.isVariantPreemptivelyForbidden !== 'function'
      ) {
        return null;
      }

      var result = conductor.isVariantPreemptivelyForbidden(String(variantId));

      var duration = performance.now() - startTime;
      trackPerformance('FORBIDDEN_CHECK', duration, { variantId: variantId });

      return result;
    } catch (error) {
      isDev() &&
        console.error('[HorizontalSlider] Forbidden check failed:', error);
      return null;
    }
  }

  /**
   * Show the warning tooltip below the slider with forbidden rule description.
   * Positions the warning tooltip at the same horizontal position as the value tooltip.
   *
   * PATTERN #17: XSS Prevention - all dynamic content sanitized via sanitizeText()
   *
   * @param {HTMLElement} container - Slider container element
   * @param {string} percentage - Current handle position as percentage string (e.g., "45%")
   * @param {Object} forbiddenResult - Result from checkSliderForbiddenState
   * @param {HTMLElement} handleEl - Handle element to add forbidden-state class
   * @param {HTMLElement} sliderEl - Slider container element for forbidden-active class
   */
  function showForbiddenWarning(
    container,
    percentage,
    forbiddenResult,
    handleEl,
    sliderEl
  ) {
    try {
      var warningTooltip = container.querySelector(
        '.' + SLIDER_CLASSES.WARNING_TOOLTIP
      );
      if (!warningTooltip) return;

      // Build warning message: warning icon + "Not Available" + rule description
      var reasons = forbiddenResult.reasons || [];
      var ruleDescription = '';
      if (reasons.length > 0) {
        // Use the first reason's description (most relevant)
        ruleDescription =
          reasons[0].reason ||
          reasons[0].ruleName ||
          'Combination not available';
      } else {
        ruleDescription =
          forbiddenResult.reasonText || 'Combination not available';
      }

      // PATTERN #17: XSS PREVENTION - Sanitize rule description before HTML insertion
      var safeDescription = sanitizeText(ruleDescription);
      warningTooltip.innerHTML = sanitizeHTML(
        '<span style="margin-right: 4px">&#9888;</span> Not Available' +
          '<br><span style="font-weight: 400; font-size: 11px; opacity: 0.9">' +
          safeDescription +
          '</span>'
      );

      // Position warning tooltip at handle percentage (mirrors value tooltip)
      warningTooltip.style.left = percentage;

      // Smart positioning: reuse the same edge-collision algorithm as value tooltip
      updateSmartTooltipPosition(warningTooltip, handleEl, percentage);

      // Show with visibility class
      warningTooltip.classList.add('visible');

      // Add forbidden state class to handle (triggers red pulse animation)
      if (handleEl) {
        handleEl.classList.add(SLIDER_CLASSES.FORBIDDEN_HANDLE);
      }

      // Add forbidden-active class to container (triggers track fill color + extra padding)
      if (sliderEl) {
        sliderEl.classList.add(SLIDER_CLASSES.FORBIDDEN_ACTIVE);
      }

      // Update ARIA for accessibility (sanitized for XSS)
      warningTooltip.setAttribute('aria-label', 'Warning: ' + ruleDescription);
    } catch (error) {
      isDev() &&
        console.error(
          '[HorizontalSlider] Error showing forbidden warning:',
          error
        );
    }
  }

  /**
   * Hide the warning tooltip and remove forbidden visual states.
   *
   * @param {HTMLElement} container - Slider container element
   * @param {HTMLElement} handleEl - Handle element to remove forbidden-state class
   * @param {HTMLElement} sliderEl - Slider container element for forbidden-active class
   */
  function hideForbiddenWarning(container, handleEl, sliderEl) {
    try {
      var warningTooltip = container.querySelector(
        '.' + SLIDER_CLASSES.WARNING_TOOLTIP
      );
      if (warningTooltip) {
        warningTooltip.classList.remove('visible');
        warningTooltip.innerHTML = '';
        warningTooltip.removeAttribute('aria-label');
      }

      // Remove forbidden state from handle
      if (handleEl) {
        handleEl.classList.remove(SLIDER_CLASSES.FORBIDDEN_HANDLE);
      }

      // Remove forbidden-active from container
      if (sliderEl) {
        sliderEl.classList.remove(SLIDER_CLASSES.FORBIDDEN_ACTIVE);
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[HorizontalSlider] Error hiding forbidden warning:',
          error
        );
    }
  }

  // ============================================================================
  // SLIDER POSITION UPDATE (SOFT BROWSING)
  // ============================================================================

  /**
   * Update slider to a new position index.
   * Updates DOM elements and triggers soft BROWSING state update.
   * (UI module - authorized for DOM manipulation per CCP architecture rules)
   *
   * PATTERN #7, #8, #9: All data access through conductor delegation
   * PATTERN #17: XSS Prevention on title content
   *
   * @param {string} categoryId - Category identifier
   * @param {number} newIndex - New position index
   * @param {Object} sliderState - Active slider state
   */
  // ============================================================================
  // C1: SLIDER COMMIT-ON-RELEASE
  // When the click_apply experience is enabled, sliders commit (apply) the
  // dragged value shortly after motion stops instead of requiring the separate
  // APPLY bar. Debounced 400ms trailing so a continuous drag commits once.
  // ============================================================================
  var SLIDER_COMMIT_DEBOUNCE_MS = 400;
  var _sliderCommitTimers = {};
  // categoryId -> true while a PENDING commit timer was scheduled by
  // seedSliderDefault() rather than a real customer interaction. Backs the
  // UI_SLIDER_DEFAULT_SEED_STARTED/COMPLETED window — see closePendingSeedWindow.
  var _sliderCommitIsSeed = {};

  /**
   * Close an open UI_SLIDER_DEFAULT_SEED_STARTED window for one category, if
   * one is open. No-ops otherwise, so every commit-timer exit path (fires,
   * gets cancelled, gets superseded by a fresh schedule) can call this
   * unconditionally without checking first.
   * @param {string} categoryId
   */
  function closePendingSeedWindow(categoryId) {
    if (!_sliderCommitIsSeed[categoryId]) return;
    delete _sliderCommitIsSeed[categoryId];
    publishEvent(EVENTS.UI_SLIDER_DEFAULT_SEED_COMPLETED, {
      categoryId: categoryId,
      timestamp: Date.now(),
      source: 'HorizontalSlider.closePendingSeedWindow',
    });
  }

  // UI modules may read the DOM directly (CCP: authorized DOM access).
  function isClickApplyEnabled() {
    try {
      var wrapper = document.querySelector(
        '.murphy-door-configurator-wrapper'
      );
      if (!wrapper) return false;
      if (wrapper.getAttribute('data-click-apply') === 'true') return true;
      var exp = wrapper.getAttribute('data-configurator-experience');
      return exp === 'dual_curated_default' || exp === 'dual_full_default';
    } catch (error) {
      return false;
    }
  }

  // ============================================================================
  // ACCEPT-THE-DEFAULT AT LOAD
  // ============================================================================
  // Ryan, UAT 2026-08-27: "Let's pre-select the width and height for the user,
  // since those need to be explicitly applied by them anyway, and if they don't
  // click the 'Use Default' text, they'll get a confusing ux."
  //
  // A measurement slider RENDERS at its default index on first load, so Width
  // already reads "30 in." before the customer touches anything -- but nothing
  // is APPLIED. The card sits incomplete, showing a value it has not committed,
  // and the only way to accept it is the quiet "Use default (30 in.)" text
  // link. This clicks it for them, once.
  //
  // 🚨 WHY THIS LIVES IN THE SLIDER AND NOT IN THE CURATED-DEFAULTS
  // COORDINATOR. That module seeds t=0 defaults from the metaobject field
  // simplified_default_variant_id, and for Width that variant is
  // 43255342170157 = 36 in. -- while the slider's own default index resolves
  // to 30 in. Measured live 2026-08-27; the two disagree. Seeding from the
  // coordinator would have silently given the customer a 36" door while the
  // slider label under it read "Use default (30 in.)". Reusing
  // sliderMetadata.defaultIndex means the applied value can never disagree
  // with the affordance it replaces, because it IS the affordance's source.
  //
  // Reuses updateSliderPosition -- the same call the "Use default" button
  // makes -- so the commit path, provenance and click-apply semantics are
  // whatever that button already does. Nothing about applying is duplicated.
  var _defaultSeeded = {};

  // Set by the SYSTEM_MAPS_INITIALIZED subscription in initialize(). Sliders
  // that render before it are seeded by that sweep; ones that render after it
  // seed themselves inline (see attachSliderEventListeners).
  var mapsInitialized = false;

  /**
   * Accept this slider's own default on the customer's behalf, once.
   * @param {string} categoryId
   * @param {Object} sliderState
   */
  function seedSliderDefault(categoryId, sliderState) {
    try {
      // Gated on the COMMIT MECHANISM, the same way syncUseDefaultVisibility
      // is: without click-apply there is a Save button, so the two-step is
      // explicit and expected, and pre-applying would fight it.
      if (!isClickApplyEnabled()) return;
      if (!categoryId || !sliderState || _defaultSeeded[categoryId]) return;

      var metadata = sliderState.sliderMetadata;
      var defaultIndex = metadata && metadata.defaultIndex;
      if (typeof defaultIndex !== 'number') return;

      // 🚨 NEVER OVERWRITE AN EXISTING ANSWER. This is what keeps the seeding
      // out of everyone else's way without re-listing which experiences do
      // what: a dual mode's curated defaults, a ?config= shared link, and a
      // customer who already moved the slider all land here as "already
      // applied", and all of them win. Checking the state instead of the
      // experience means a new seeding source added later cannot be
      // double-applied by this one.
      var conductor = sliderState.conductor;
      var applied =
        conductor && typeof conductor.getAppliedSelections === 'function'
          ? conductor.getAppliedSelections()
          : null;
      if (applied && applied[categoryId]) return;

      _defaultSeeded[categoryId] = true;

      // 🚨 THIS IS NOT A CUSTOMER INTERACTION. updateSliderPosition() below
      // reuses the exact same commit path a real drag/click/"Use default"
      // press takes, so the SELECTION_BROWSING/SELECTION_APPLIED events it
      // produces are indistinguishable from one -- a module that auto-toggles
      // UI in response to "the customer touched a measurement category" (the
      // blueprint overlay's measurement-mode switch) cannot tell the
      // difference on its own. Bracket the whole window -- from here through
      // the click-apply-debounced commit actually landing -- with a
      // dedicated, narrowly-scoped event pair so such a module can ignore it.
      // See UI_SLIDER_DEFAULT_SEED_STARTED's definition for why this is its
      // own event rather than the general bulk-cascade one.
      publishEvent(EVENTS.UI_SLIDER_DEFAULT_SEED_STARTED, {
        categoryId: categoryId,
        timestamp: Date.now(),
        source: 'HorizontalSlider.seedSliderDefault',
      });
      updateSliderPosition(categoryId, defaultIndex, sliderState, true);
    } catch (error) {
      // A failed seed must never break the slider it was seeding. If the
      // failure happened after UI_SLIDER_DEFAULT_SEED_STARTED was published
      // (the only way to reach this catch once that line has run), the
      // window must still close -- an unclosed window is a worse defect than
      // a stray extra COMPLETED for a STARTED that never actually opened one.
      publishEvent(EVENTS.UI_SLIDER_DEFAULT_SEED_COMPLETED, {
        categoryId: categoryId,
        timestamp: Date.now(),
        source: 'HorizontalSlider.seedSliderDefault.error',
      });
      isDev() &&
        console.warn('[HorizontalSlider] seedSliderDefault failed:', error);
    }
  }

  /** Seed every slider that has rendered so far. */
  function seedAllSliderDefaults() {
    activeSliders.forEach(function (sliderState, categoryId) {
      seedSliderDefault(categoryId, sliderState);
    });
  }

  /**
   * Drop a scheduled-but-unfired commit for one category.
   *
   * Called when something OTHER than this slider applies the category, at which
   * point the variant the pending timer captured is stale by definition. See the
   * SELECTION_APPLIED subscriber in initialize() for why this is cancelled at
   * the source rather than left to the conductor's stale-commit guard.
   *
   * @param {string} categoryId
   * @returns {boolean} true if a pending commit was actually cancelled
   */
  function cancelSliderCommit(categoryId) {
    if (!categoryId || !_sliderCommitTimers[categoryId]) return false;
    clearTimeout(_sliderCommitTimers[categoryId]);
    delete _sliderCommitTimers[categoryId];
    // A cancelled commit never reaches the setTimeout callback below, so if it
    // was a seeded default's pending commit, close the window here instead —
    // otherwise it would stay open for the rest of the session.
    closePendingSeedWindow(categoryId);
    isDev() &&
      console.log(
        '[HorizontalSlider] Cancelled pending commit for ' +
          categoryId +
          ' (applied elsewhere)'
      );
    return true;
  }

  function scheduleSliderCommit(categoryId, variant, isDefaultSeed) {
    if (!categoryId || !variant || !variant.variantId) return;
    if (_sliderCommitTimers[categoryId]) {
      clearTimeout(_sliderCommitTimers[categoryId]);
      // Being superseded by a fresh schedule -- if the old timer belonged to
      // a seeded default, its window closes now rather than leaking into
      // whatever this new commit turns out to be.
      closePendingSeedWindow(categoryId);
    }
    var isSeed = !!isDefaultSeed;
    _sliderCommitIsSeed[categoryId] = isSeed;
    _sliderCommitTimers[categoryId] = setTimeout(function () {
      delete _sliderCommitTimers[categoryId];
      publishEvent(EVENTS.UI_SLIDER_COMMIT_REQUESTED, {
        categoryId: categoryId,
        variantId: variant.variantId,
        productId: variant.productId,
        // true ONLY for seedSliderDefault()'s bootstrap commit (the default
        // accepted on the customer's behalf). The conductor applies it with
        // source 'slider_seed' -> provenance 'curated_default', so an
        // untouched Width / Height / Door Depth / Extra Shelves never reads
        // as hand-picked. A drag / key / click that supersedes the seed
        // schedules a fresh timer with isSeed false: 'slider_commit', 'user'.
        seed: isSeed,
        timestamp: Date.now(),
        source: 'HorizontalSlider.scheduleSliderCommit',
      });
      // UI_SLIDER_COMMIT_REQUESTED is handled synchronously by the conductor
      // (APPLY_SELECTION -> SELECTION_APPLIED all happen inside the publish()
      // call above), so closing the seed window AFTER it keeps the window
      // open for the very event it exists to cover.
      closePendingSeedWindow(categoryId);
    }, SLIDER_COMMIT_DEBOUNCE_MS);
  }

  /**
   * Shows "Use default (X)" only while the slider is sitting ON its default,
   * where it is the only way to commit the category. The moment the customer
   * moves away from the default the Save button takes over and this hides.
   *
   * Uses the hidden class rather than an inline style so it cannot fight the
   * repo's inline-CSS CI gate, and so the button keeps its layout box rules
   * from section-md-configurator.css.
   * @param {Object} sliderState
   * @param {number} currentIndex
   */
  function syncUseDefaultVisibility(sliderState, currentIndex) {
    try {
      var container = sliderState && sliderState.container;
      if (!container) return;
      var btn = container.querySelector('.' + SLIDER_CLASSES.USE_DEFAULT);
      if (!btn) return; // no default configured for this category

      var defaultIndex =
        sliderState.sliderMetadata &&
        sliderState.sliderMetadata.defaultIndex;
      if (typeof defaultIndex !== 'number') return;

      // The click-apply force-show branch that used to sit here was removed
      // 2026-08-27 with the button itself: under click-apply the button is no
      // longer rendered at all, so the `if (!btn) return` above always fires
      // first and this code could never run. Everything below is the
      // non-click-apply swap, which is the only case that still has a button.

      var atDefault = currentIndex === defaultIndex;
      btn.classList.toggle('hidden', !atDefault);
      // Keep it out of the tab order when hidden -- a focusable control the
      // customer cannot see is the same defect class the P7 pass just closed.
      btn.setAttribute('aria-hidden', atDefault ? 'false' : 'true');
      if (atDefault) {
        btn.removeAttribute('tabindex');
      } else {
        btn.setAttribute('tabindex', '-1');
      }
    } catch (error) {
      isDev() &&
        console.warn(
          '[HorizontalSlider] syncUseDefaultVisibility failed:',
          error
        );
    }
  }

  /**
   * @param {string} categoryId
   * @param {number} newIndex
   * @param {Object} sliderState
   * @param {boolean} [isDefaultSeed] - True when this call originated from
   *   seedSliderDefault() (the bootstrap "accept the default on the
   *   customer's behalf" seed) rather than a real drag/click. Threaded only
   *   as far as scheduleSliderCommit(), which uses it to bracket the
   *   UI_SLIDER_DEFAULT_SEED_STARTED/COMPLETED window.
   */
  function updateSliderPosition(categoryId, newIndex, sliderState, isDefaultSeed) {
    var startTime = performance.now();
    var metadata = sliderState.sliderMetadata;
    var variant = metadata.sortedVariants[newIndex];

    if (!variant) {
      isDev() &&
        console.warn(
          '[HorizontalSlider] Invalid index ' + newIndex + ' for ' + categoryId
        );
      // Should be unreachable from seedSliderDefault() (defaultIndex is read
      // from this same metadata object), but an unclosed seed window is worse
      // than a defensive publish here.
      if (isDefaultSeed) {
        publishEvent(EVENTS.UI_SLIDER_DEFAULT_SEED_COMPLETED, {
          categoryId: categoryId,
          timestamp: Date.now(),
          source: 'HorizontalSlider.updateSliderPosition',
        });
      }
      return;
    }

    // Update internal state
    sliderState.currentIndex = newIndex;

    // Re-register the slider the customer is moving. The SELECTION_APPLIED
    // subscriber below drops a category from activeSliders on every apply (so
    // re-opening it re-renders), which also hid it from the preemptive
    // forbidden subscriber: once an applied slider was moved onto a forbidden
    // stop and its commit was rejected, nothing re-evaluated that stop when
    // another choice made it allowed again, and the warning never cleared.
    // A successful commit removes it again through the same subscriber.
    if (activeSliders.get(categoryId) !== sliderState) {
      activeSliders.set(categoryId, sliderState);
    }

    // "Use default" and the Save button are alternatives, never both.
    //
    // WHY: measured 2026-08-20 on an untouched slider -- the Save button is
    // rendered but display:none, and only revealed once a change puts the
    // category into browsing (updateApplyButtonVisibility's callers are all
    // browsing-driven). So at the default there is NO way to commit, which is
    // exactly why "Use default" exists; and after an adjustment BOTH controls
    // would otherwise be visible at once, offering two ways to do one thing.
    //
    // Not experience-gated on purpose. updateApplyButtonVisibility is not
    // gated either, so apo_style reveals the Save button on adjustment just
    // like classic and dual do -- scoping this to non-apo would leave apo as
    // the only mode still showing both.
    syncUseDefaultVisibility(sliderState, newIndex);

    // Calculate percentage
    var percentage =
      metadata.totalSteps > 1
        ? (newIndex / (metadata.totalSteps - 1)) * 100
        : 50;

    // Update DOM elements (UI module - authorized DOM access)
    var container = sliderState.container;
    var sliderEl = container.querySelector('.' + SLIDER_CLASSES.CONTAINER);
    var handle = container.querySelector('.' + SLIDER_CLASSES.HANDLE);
    var trackFill = container.querySelector('.' + SLIDER_CLASSES.TRACK_FILL);
    var tooltip = container.querySelector('.' + SLIDER_CLASSES.TOOLTIP);
    var minusBtn = container.querySelector('.' + SLIDER_CLASSES.BUTTON_MINUS);
    var plusBtn = container.querySelector('.' + SLIDER_CLASSES.BUTTON_PLUS);

    if (handle) {
      handle.style.left = percentage + '%';
      handle.setAttribute('data-index', newIndex);
    }

    if (trackFill) {
      trackFill.style.width = percentage + '%';
    }

    if (tooltip) {
      // Title + price (PATTERN #17 sanitization lives in buildTooltipHTML).
      tooltip.innerHTML = sanitizeHTML(
        buildTooltipHTML(variant, sliderState.conductor)
      );

      // Smart positioning: calculate arrow offset to keep pointing at handle (TooltipManager pattern)
      updateSmartTooltipPosition(tooltip, handle, percentage + '%');
    }

    // Update ARIA attributes (on the role="slider" track; see renderSlider)
    if (sliderEl) {
      var ariaSlider = sliderEl.querySelector('[role="slider"]');
      if (ariaSlider) {
        ariaSlider.setAttribute('aria-valuenow', newIndex);
        ariaSlider.setAttribute(
          'aria-valuetext',
          getSliderValueText(variant, sliderState.conductor)
        );
      }
      sliderEl.setAttribute('data-current-index', newIndex);
    }

    // Update button disabled states
    if (minusBtn) {
      minusBtn.disabled = newIndex === 0;
    }
    if (plusBtn) {
      plusBtn.disabled = newIndex === metadata.totalSteps - 1;
    }

    // ============================================================================
    // FORBIDDEN VALIDATION CHECK (CCP-compliant via conductor delegation)
    // Check if new slider position is forbidden based on current applied selections.
    // Uses O(1) preemptive forbidden state lookup - no raw data access.
    // PATTERN #9: Data access through conductor delegation
    // ============================================================================
    var isForbidden = false;
    var forbiddenResult = null;

    if (sliderState.conductor) {
      forbiddenResult = checkSliderForbiddenState(
        variant.variantId,
        sliderState.conductor
      );
      isForbidden =
        forbiddenResult !== null && forbiddenResult.isForbidden === true;

      if (isForbidden) {
        // Show warning tooltip + red handle pulse
        showForbiddenWarning(
          container,
          percentage + '%',
          forbiddenResult,
          handle,
          sliderEl
        );

        // PATTERN #10: Publish forbidden state change event for UI coordination
        publishEvent(EVENTS.UI_SLIDER_FORBIDDEN_STATE_CHANGED, {
          categoryId: categoryId,
          variantId: variant.variantId,
          variantTitle: variant.title,
          isForbidden: true,
          reasons: forbiddenResult.reasons,
          reasonText: forbiddenResult.reasonText,
          sliderIndex: newIndex,
          timestamp: Date.now(),
          source: 'HorizontalSlider.updateSliderPosition',
        });

        isDev() &&
          console.log(
            '[HorizontalSlider] Forbidden value detected at index ' +
              newIndex +
              ' for ' +
              categoryId +
              ': ' +
              forbiddenResult.reasonText
          );
      } else {
        // Clear any existing forbidden warning
        hideForbiddenWarning(container, handle, sliderEl);

        // FIX: Publish isForbidden:false ONLY when transitioning FROM forbidden state
        // (mirrors the preemptive update handler pattern at lines ~1249-1260)
        // Without this, ui-updater never restores disabled=false on the APPLY button
        if (sliderState.isForbidden) {
          publishEvent(EVENTS.UI_SLIDER_FORBIDDEN_STATE_CHANGED, {
            categoryId: categoryId,
            variantId: variant.variantId,
            isForbidden: false,
            trigger: 'slider_position_change',
            timestamp: Date.now(),
            source: 'HorizontalSlider.updateSliderPosition',
          });
        }
      }
    }

    // Store forbidden state on sliderState for APPLY button integration
    sliderState.isForbidden = isForbidden;
    sliderState.forbiddenResult = forbiddenResult;

    // SOFT BROWSING UPDATE: Update variant in existing BROWSING state
    // This prevents APPLY button flicker by not exiting/re-entering BROWSING
    // PATTERN #7, #8: Access state manager via conductor delegation (NOT direct namespace)
    var conductor = sliderState.conductor;

    // 🚨 THE SOFT UPDATE IS A NO-OP WITH NO BROWSING STATE TO UPDATE, AND IT
    // FAILS SILENTLY. md-config-core-state-manager's updateBrowsingSelection
    // opens with `if (!browsingState) return;`, and the conductor's
    // stale-commit guard then refuses the 400ms commit because there is no
    // browsing entry to match the committed variant against. Net effect: the
    // handle moves, the tooltip updates, and NOTHING is ever applied.
    //
    // That became reachable the moment renderSlider stopped entering BROWSING
    // for an already-applied category (see the guard there). A customer opening
    // a shared ?config= link could move the Width slider and watch it do
    // nothing. Caught in production UAT by pressing the arrow key twice and
    // sampling the applied map -- never by a unit test, because the harness
    // stubbed updateBrowsingSelection and so could not reproduce its early
    // return. The fixed harness now models it.
    //
    // Entering BROWSING on the customer's FIRST MOVE rather than at render is
    // also the semantically correct place for it: rendering a slider is not
    // browsing, moving one is. enterBrowsingStateForSlider carries
    // suppressClickApply, so this still commits on release rather than
    // instantly.
    if (browsingEntryFor(conductor, categoryId) === null) {
      enterBrowsingStateForSlider(
        categoryId,
        variant,
        sliderState.container,
        conductor
      );
    } else if (
      conductor &&
      conductor.stateManager &&
      typeof conductor.stateManager.updateBrowsingSelection === 'function'
    ) {
      // PATTERN #9: Build display data from PPD via conductor delegation
      var ppd = conductor.getPerfectProductDictionary();
      var displayKey = 'variant_' + variant.variantId;
      var displayData =
        ppd && ppd.selectionDisplayDataMap
          ? ppd.selectionDisplayDataMap.get(displayKey)
          : null;

      conductor.stateManager.updateBrowsingSelection(
        categoryId,
        variant.variantId,
        variant.productId,
        {
          displayTitle: displayData ? displayData.displayTitle : variant.title,
          price: displayData ? displayData.price : variant.price,
          productTitle: displayData ? displayData.productTitle : variant.title,
        }
      );
    }

    // Also update pricing via conductor delegation (runs shipping/price/space calculations)
    if (conductor && typeof conductor.handlePriceUpdates === 'function') {
      conductor.handlePriceUpdates(
        {
          productId: variant.productId,
          variantId: variant.variantId,
          categoryId: categoryId,
        },
        'browsing'
      );
    }

    // Performance tracking
    var duration = performance.now() - startTime;
    trackPerformance('DRAG_UPDATE', duration, {
      categoryId: categoryId,
      newIndex: newIndex,
    });

    // PATTERN #10: UI Update Event for position change coordination
    publishEvent(EVENTS.UI_SLIDER_DRAG_MOVE, {
      categoryId: categoryId,
      index: newIndex,
      variantId: variant.variantId,
      title: variant.title,
      numericValue: variant.numericValue,
      duration: duration.toFixed(2),
      timestamp: Date.now(),
    });

    // C1: when click=apply is on, commit the slider value 400ms after motion
    // settles (covers drag, keyboard, +/- buttons, and track-click uniformly).
    if (isClickApplyEnabled()) {
      scheduleSliderCommit(categoryId, variant, isDefaultSeed);
    } else if (isDefaultSeed) {
      // seedSliderDefault() only ever calls in here with click-apply enabled
      // (it early-returns otherwise), but if that ever changes, closing the
      // window immediately beats leaking it open for the rest of the session.
      // No commit was scheduled, so _sliderCommitIsSeed was never set — go
      // straight to the source event rather than the guarded helper, which
      // would no-op here.
      publishEvent(EVENTS.UI_SLIDER_DEFAULT_SEED_COMPLETED, {
        categoryId: categoryId,
        timestamp: Date.now(),
        source: 'HorizontalSlider.updateSliderPosition',
      });
    }
  }

  // ============================================================================
  // BROWSING STATE ENTRY
  // ============================================================================

  /**
   * Enter BROWSING state for the initial/default slider variant.
   * Uses the SelectionCoordinator for CCP-compliant BROWSING entry.
   *
   * PATTERN #14: Try/catch + FAILED event on error
   *
   * @param {string} categoryId - Category identifier
   * @param {Object} targetVariant - Variant data from PPD slider metadata
   * @param {HTMLElement} container - Slider container element
   * @param {Object} conductor - Conductor instance
   */
  function enterBrowsingStateForSlider(
    categoryId,
    targetVariant,
    container,
    conductor
  ) {
    var startTime = performance.now();
    var METHOD_SIGNATURE = 'HorizontalSlider.enterBrowsingState';

    try {
      var selectionCoordinator =
        window.MurphyDoorConfigurator.selectionCoordinator;

      if (
        selectionCoordinator &&
        typeof selectionCoordinator.enterBrowsingState === 'function'
      ) {
        // Use the SelectionCoordinator for proper CCP-compliant BROWSING entry
        var sliderContainer = container.querySelector(
          '.' + SLIDER_CLASSES.CONTAINER
        );

        selectionCoordinator
          .enterBrowsingState(
            {
              productId: targetVariant.productId,
              variantId: targetVariant.variantId,
              categoryId: categoryId,
              element: sliderContainer || container,
              autoApplied: false,
              // C1: the slider's initial render browsing entry must NOT
              // click-apply the untouched default — it commits on drag release.
              suppressClickApply: true,
            },
            conductor
          )
          .then(function (result) {
            if (result && result.success) {
              isDev() &&
                console.log(
                  '[HorizontalSlider] BROWSING state entered for ' +
                    categoryId +
                    ': ' +
                    targetVariant.title
                );
            } else {
              isDev() &&
                console.warn(
                  '[HorizontalSlider] BROWSING state entry returned non-success for ' +
                    categoryId +
                    ':',
                  result
                );
            }

            // Performance tracking
            var duration = performance.now() - startTime;
            trackPerformance('BROWSING_ENTRY', duration, {
              categoryId: categoryId,
            });
          })
          .catch(function (error) {
            // PATTERN #14: FAILED event on catch
            publishEvent(EVENTS.UI_SLIDER_RENDERED, {
              categoryId: categoryId,
              stage: 'failed',
              error: 'BROWSING state entry failed: ' + error.message,
              timestamp: Date.now(),
              source: METHOD_SIGNATURE,
            });
            isDev() &&
              console.error(
                '[HorizontalSlider] BROWSING state entry failed for ' +
                  categoryId +
                  ':',
                error
              );
          });
      } else {
        // Fallback: Direct conductor handleStateChange (less ideal but functional)
        isDev() &&
          console.warn(
            '[HorizontalSlider] SelectionCoordinator not available, using direct conductor delegation'
          );
        conductor.handleStateChange('SELECTION_BROWSING', {
          productId: targetVariant.productId,
          variantId: targetVariant.variantId,
          categoryId: categoryId,
          element: container,
          productType: 'multi-variant',
          // C1: this is the slider's initial render browsing entry — never
          // click-apply it. The slider commits on drag release instead.
          suppressClickApply: true,
        });
      }

      // PATTERN #10: UI Ready event for coordination
      publishEvent(EVENTS.UI_SLIDER_READY, {
        categoryId: categoryId,
        variantId: targetVariant.variantId,
        variantTitle: targetVariant.title,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });
    } catch (error) {
      // PATTERN #14: FAILED event on catch
      publishEvent(EVENTS.UI_SLIDER_RENDERED, {
        categoryId: categoryId,
        stage: 'failed',
        error: 'enterBrowsingState failed: ' + error.message,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });
      isDev() &&
        console.error(
          '[HorizontalSlider] Error entering BROWSING state for ' +
            categoryId +
            ':',
          error
        );
    }
  }

  /**
   * DEAD-END FIX (2026-09-29): the stop the slider is SHOWING just went from
   * forbidden to allowed, and nothing applies it.
   *
   * Land the Door Depth slider on 12" at a width under 30": the conductor
   * rejects the commit and the category stays unapplied (correct). Widen the
   * door to 30" or more and the warning clears -- but the slider still shows
   * 12" unapplied, Add to Cart stays disabled with "1 choice left", and the
   * customer has to nudge the slider off and back to get it applied. Prod's
   * old tiles simply disabled the 12" tile, so this was a regression there.
   *
   * Commits through the SAME path a real move uses (scheduleSliderCommit ->
   * UI_SLIDER_COMMIT_REQUESTED -> conductor APPLY_SELECTION), so the
   * conductor still re-validates the stop against the forbidden rules and its
   * stale-commit guard still has the final say.
   *
   * Only in click-apply (classic keeps its explicit Save, which the cleared
   * warning has just re-enabled), and only when the category is not already
   * applied at this stop. A missing browse entry is re-entered first, because
   * the conductor refuses any commit it cannot match to the current browse.
   *
   * @param {string} categoryId
   * @param {Object} variant - the stop the slider is showing
   * @param {Object} sliderState
   */
  function commitNowAllowedStop(categoryId, variant, sliderState) {
    try {
      if (!isClickApplyEnabled()) return;
      if (!variant || !variant.variantId || !sliderState) return;
      var conductor = sliderState.conductor;
      if (isAppliedAtVariant(conductor, categoryId, variant.variantId)) return;

      var browse = browsingEntryFor(conductor, categoryId);
      if (browse === null) {
        enterBrowsingStateForSlider(
          categoryId,
          variant,
          sliderState.container,
          conductor
        );
      } else if (
        browse.variantId != null &&
        String(browse.variantId) !== String(variant.variantId)
      ) {
        // Something else is browsing a different option in this category;
        // committing the slider's stop would overwrite it.
        return;
      }

      isDev() &&
        console.log(
          '[HorizontalSlider] ' +
            categoryId +
            ' stop is no longer forbidden - committing ' +
            variant.title
        );
      scheduleSliderCommit(categoryId, variant, false);
    } catch (error) {
      isDev() &&
        console.warn(
          '[HorizontalSlider] commitNowAllowedStop failed for ' +
            categoryId +
            ':',
          error
        );
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * After an apply on a DERIVED_PRODUCT_RULES source (or the derived category
   * itself), re-render the derived slider only if the product it implies has
   * CHANGED -- Shelf Style Standard -> Lipped swaps Extra Shelves' stops and
   * prices. A plain commit on the same product never re-renders, so the
   * slider keeps keyboard focus. Deferred one tick so the derived-product
   * coordinator's same-count correction lands first; focus is restored if
   * the customer was on the slider.
   * @param {string} appliedCategoryId
   */
  function scheduleDerivedSliderRefresh(appliedCategoryId) {
    var rules = CONSTANTS.DERIVED_PRODUCT_RULES || {};
    Object.keys(rules).forEach(function (derivedCategoryId) {
      var rule = rules[derivedCategoryId];
      if (
        derivedCategoryId !== appliedCategoryId &&
        !(rule && rule.sourceCategory === appliedCategoryId)
      ) {
        return;
      }
      var rendered = derivedSliderRenders.get(derivedCategoryId);
      if (!rendered) return;
      setTimeout(function () {
        try {
          var conductor = window.MurphyDoorConfigurator.conductor;
          if (!conductor || !rendered.container.isConnected) return;
          var view = conductor.getSliderMetadata(derivedCategoryId);
          var first = view && view.sortedVariants && view.sortedVariants[0];
          if (!first || String(first.productId) === rendered.productId) return;
          var hadFocus = rendered.container.contains(document.activeElement);
          activeSliders.delete(derivedCategoryId);
          renderSlider(derivedCategoryId, rendered.container, conductor);
          if (hadFocus) {
            var slider = rendered.container.querySelector(
              '.' + SLIDER_CLASSES.CONTAINER + ' [role="slider"]'
            );
            if (slider) slider.focus();
          }
        } catch (error) {
          isDev() &&
            console.error(
              '[HorizontalSlider] Derived slider refresh failed for ' +
                derivedCategoryId,
              error
            );
        }
      }, 0);
    });
  }

  function initialize() {
    // PATTERN #16: Initialization guard
    if (isInitialized) {
      isDev() &&
        console.warn('[HorizontalSlider] Already initialized, skipping');
      return;
    }

    // ACCEPT-THE-DEFAULT: fire on SYSTEM_MAPS_INITIALIZED, not on the earlier
    // SYSTEM_STATE_FROM_PPD_READY this module already gates its rendering on.
    //
    // 🚨 THE EVENT CHOICE IS THE WHOLE FIX, and it is measured, not guessed.
    // Instrumenting a real load showed the ordering is
    //   system:state_from_ppd_ready  @3781ms
    //   system:maps_initialized      @3939ms
    // and an APPLY_SELECTION issued in that 158ms window lands on state maps
    // that are then re-initialised -- it vanishes, silently, with the category
    // still reading incomplete. A first attempt at this feature applied on the
    // earlier event and did exactly that: the payload was correct, the applies
    // ran, and getAppliedSelections() came back empty.
    //
    // Sliders that render AFTER this event are covered by the seed call at the
    // end of attachSliderEventListeners; sliders that rendered before are
    // covered by this sweep. Both funnel through seedSliderDefault's
    // already-applied and once-only guards, so the overlap is harmless.
    if (CoreEventBus && EVENTS.SYSTEM_MAPS_INITIALIZED) {
      var unsubMapsReady = CoreEventBus.subscribe(
        EVENTS.SYSTEM_MAPS_INITIALIZED,
        function () {
          mapsInitialized = true;
          // 🚨 DEFERRED BY A MACROTASK, and this is measured too. Subscribers
          // to this event run synchronously in registration order, and the
          // state manager is still finishing its own maps work while ours
          // runs -- seeding inline lands in the same doomed window as seeding
          // on the earlier bootstrap event, and vanishes identically. A
          // setTimeout(0) lets every other subscriber of this event finish
          // first. Verified both ways on a real load: inline = nothing
          // applied, deferred = Width and Height applied and completed.
          setTimeout(seedAllSliderDefaults, 0);
        }
      );
      cleanupHandlers.push(unsubMapsReady);
    }

    // PATTERN #4: Event Consumer Handler - Re-evaluate forbidden state when
    // preemptive forbidden calculation completes (triggered by other category changes).
    // This ensures slider forbidden warnings update dynamically as users apply selections.
    if (CoreEventBus) {
      var unsubPreemptive = CoreEventBus.subscribe(
        EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE,
        function (eventData) {
          // Re-evaluate all active sliders against the updated forbidden state
          activeSliders.forEach(function (sliderState, categoryId) {
            if (!sliderState || !sliderState.conductor) return;

            var metadata = sliderState.sliderMetadata;
            var variant = metadata.sortedVariants[sliderState.currentIndex];
            if (!variant) return;

            var container = sliderState.container;
            var handle = container.querySelector('.' + SLIDER_CLASSES.HANDLE);
            var sliderEl = container.querySelector(
              '.' + SLIDER_CLASSES.CONTAINER
            );

            var forbiddenResult = checkSliderForbiddenState(
              variant.variantId,
              sliderState.conductor
            );
            var isForbidden =
              forbiddenResult !== null && forbiddenResult.isForbidden === true;

            if (isForbidden) {
              var percentage =
                metadata.totalSteps > 1
                  ? (sliderState.currentIndex / (metadata.totalSteps - 1)) * 100
                  : 50;
              showForbiddenWarning(
                container,
                percentage + '%',
                forbiddenResult,
                handle,
                sliderEl
              );

              // PATTERN #10: Publish state change event for UI coordination
              publishEvent(EVENTS.UI_SLIDER_FORBIDDEN_STATE_CHANGED, {
                categoryId: categoryId,
                variantId: variant.variantId,
                isForbidden: true,
                reasons: forbiddenResult.reasons,
                reasonText: forbiddenResult.reasonText,
                trigger: 'preemptive_update',
                timestamp: Date.now(),
                source: 'HorizontalSlider.preemptiveUpdateHandler',
              });
            } else if (sliderState.isForbidden) {
              // Was forbidden, now cleared
              hideForbiddenWarning(container, handle, sliderEl);

              publishEvent(EVENTS.UI_SLIDER_FORBIDDEN_STATE_CHANGED, {
                categoryId: categoryId,
                variantId: variant.variantId,
                isForbidden: false,
                trigger: 'preemptive_update',
                timestamp: Date.now(),
                source: 'HorizontalSlider.preemptiveUpdateHandler',
              });

              commitNowAllowedStop(categoryId, variant, sliderState);
            }

            sliderState.isForbidden = isForbidden;
            sliderState.forbiddenResult = forbiddenResult;
          });
        }
      );
      cleanupHandlers.push(unsubPreemptive);

      // NOTE: APPLY button visual state updates have been moved to md-config-ui-updater.js
      // to avoid tight coupling between slider and button components. The slider still
      // publishes UI_SLIDER_FORBIDDEN_STATE_CHANGED events for other modules to consume.

      // PATTERN #4: Event Consumer Handler - Clear stale activeSliders entry when a
      // slider selection is APPLIED. Without this, re-opening the category hits the
      // skip-re-render guard (line ~366) because activeSliders still has the entry,
      // preventing renderSlider from calling enterBrowsingStateForSlider and making
      // the APPLY button visible again.
      var unsubApplied = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_APPLIED,
        function (eventData) {
          if (!eventData || !eventData.categoryId) return;
          var appliedCategoryId = eventData.categoryId;

          // 🚨 CANCEL ANY PENDING COMMIT FOR A CATEGORY SOMEONE ELSE JUST
          // ANSWERED. scheduleSliderCommit captures the variant it was called
          // with and fires 400ms later; anything that applies this category in
          // the meantime -- a ?config= restore, cascade auto-selection, a
          // curated default -- makes that captured variant STALE, and a stale
          // commit is never the customer's intent.
          //
          // Today the conductor's own stale-commit guard refuses it (it compares
          // the committed variant against the current browsing selection), so
          // the visible symptom is a dimension that goes quietly unapplied
          // rather than a wrong one. But that guard is the LAST line, not the
          // first, and it inverts the moment browsing and applied disagree:
          // publish a commit for a variant that browsing happens to still hold
          // and the seeded DEFAULT overwrites the restored value -- a customer
          // opening their own shared 37" link and being handed a 36" door.
          // Cancelling here retires the race instead of adjudicating it.
          //
          // Cancelling on the slider's OWN commit is a harmless no-op: the timer
          // deletes its own key before publishing, so there is nothing left to
          // clear by the time SELECTION_APPLIED comes back around.
          cancelSliderCommit(appliedCategoryId);

          scheduleDerivedSliderRefresh(appliedCategoryId);
          if (activeSliders.has(appliedCategoryId)) {
            activeSliders.delete(appliedCategoryId);
            isDev() &&
              console.log(
                '[HorizontalSlider] Cleared activeSliders entry for applied category: ' +
                  appliedCategoryId
              );
          }
        }
      );
      cleanupHandlers.push(unsubApplied);
    }

    isInitialized = true;

    // PATTERN #5: Event Replay Ready - Publish initialization event so late
    // subscribers can query current slider state
    publishEvent(EVENTS.UI_SLIDER_INITIALIZED, {
      timestamp: Date.now(),
      source: 'HorizontalSlider.initialize',
      activeSliderCount: activeSliders.size,
    });

    isDev() &&
      console.log(
        '[HorizontalSlider] Initialized with bootstrap barrier protection and forbidden validation'
      );
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  function cleanup() {
    isDev() && console.log('[HorizontalSlider] Cleaning up resources');

    // Clear active slider states
    activeSliders.clear();

    // C1: cancel any pending debounced slider-commit timers so an orphaned
    // timer can't fire a stale APPLY_SELECTION after teardown.
    Object.keys(_sliderCommitTimers).forEach(cancelSliderCommit);

    // Unsubscribe all event handlers (Pattern #15)
    cleanupHandlers.forEach(function (unsubscribe) {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    cleanupHandlers.length = 0;

    isInitialized = false;

    // PATTERN #10: Publish cleanup event for coordination
    publishEvent(EVENTS.UI_SLIDER_CLEANUP, {
      timestamp: Date.now(),
      source: 'HorizontalSlider.cleanup',
    });
  }

  // ============================================================================
  // PATTERN #20: SINGLE NAMESPACE PUBLICATION (10 points)
  // MODULE EXPORT
  // ============================================================================

  window.MurphyDoorConfigurator.horizontalSlider = {
    renderSlider: function (categoryId, container, conductor) {
      ensureBootstrap(renderSlider, null, [categoryId, container, conductor]);
    },
    initialize: initialize,
    cleanup: cleanup,
    // Expose for testing
    _getActiveSliders: function () {
      return activeSliders;
    },
    _getPerformanceThresholds: function () {
      return PERFORMANCE_THRESHOLDS;
    },
    _checkSliderForbiddenState: function (variantId, conductor) {
      return checkSliderForbiddenState(variantId, conductor);
    },
    // Test seams for the tooltip / aria-valuetext text (pure given utils).
    _buildTooltipHTML: buildTooltipHTML,
    _getSliderValueText: getSliderValueText,
  };

  // Auto-initialize
  initialize();
})(window);
